import {
  LangChangeEvent,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { LanguageService }       from '../../../core/services/utils/language.service';
import { SwalService }           from '../../../core/services/utils/swal.service';
import { ButtonComponent }       from '../../../components/ui/button/button.component';
import { CustomTabComponent }    from '../../../components/ui/custom-tab/custom-tab.component';
import { ModalComponent }        from '../../../components/ui/modal/modal.component';
import { AcademicPeriodService } from '../../../core/services/academic-period.service';
import { AcademicPeriod }        from '../../../core/interfaces/academic-period';
import { AuthService }           from '../../../core/services/utils/auth.service';
import { ReadOnlyNoticeComponent } from '../../../components/ui/read-only-notice/read-only-notice.component';
import { Component, inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';

@Component({
  selector: 'app-academic-periods',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    ButtonComponent,
    CustomTabComponent,
    ModalComponent,
    ReadOnlyNoticeComponent,
  ],
  templateUrl: './academic-periods.component.html',
  styleUrl: './academic-periods.component.css',
})
export class AcademicPeriodsComponent implements OnInit, OnDestroy {

  private translate       = inject(TranslateService);
  private languageService = inject(LanguageService);
  private periodService   = inject(AcademicPeriodService);
  private swalService     = inject(SwalService);
  private readonly destroy$ = new Subject<void>();
  private cdr = inject(ChangeDetectorRef);

  /**
   * Les périodes se lisent avec `referential.read` et se règlent avec une autre permission. Sans `referential.write`, l'écran s'ouvre en consultation : champs
   * figés, gestes d'écriture retirés, et un bandeau qui le dit. Le back
   * revérifie : ce test n'est pas la protection, c'est l'honnêteté de l'écran.
   */
  readonly canWrite = inject(AuthService).hasPermission('referential.write');

  // ── state ─────────────────────────────────────────────────────────────────
  periods: AcademicPeriod[] = [];
  periodsForDisplay: any[] = [];   // ✅ données transformées pour l'affichage
  isLoading     = false;
  isSaving      = false;
  error: string | null = null;
  isRTL         = false;
  currentLocale = 'ar';

  // ── view modal ────────────────────────────────────────────────────────────
  showViewModal = false;
  viewPeriod: AcademicPeriod | null = null;

  // ── add modal ─────────────────────────────────────────────────────────────
  showAddModal = false;
  newPeriod = { name: '', startDate: '', endDate: '' };

  // ── edit modal ────────────────────────────────────────────────────────────
  showEditModal  = false;
  selectedPeriod: AcademicPeriod | null = null;
  editPeriodData = { name: '', startDate: '', endDate: '' };

  // ── columns ───────────────────────────────────────────────────────────────
  translatedHeaders = {
    name:      '',
    startDate: '',
    endDate:   '',
    status:    '',
  };

  periodColumns: Array<{
    field:      string;
    headerText: string;
    width:      number;
    textAlign?: string;
    template?:  string;
    freeze?:    string;
  }> = [];

  // ─────────────────────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.initializeComponent();
    this.subscribeToLanguageChanges();
    this.loadPeriods();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── init ──────────────────────────────────────────────────────────────────
  private initializeComponent(): void {
    this.currentLocale = this.languageService.getCurrentLang();
    this.isRTL         = !this.languageService.isLTR();
    this.loadTranslations();
  }

private subscribeToLanguageChanges(): void {
  this.translate.onLangChange
    .pipe(takeUntil(this.destroy$))
    .subscribe((event: LangChangeEvent) => {
      this.currentLocale = event.lang;
      this.isRTL         = !this.languageService.isLTR();
      this.loadTranslations();
      // ✅ Recharger complètement depuis l'API
      this.loadPeriods();
    });
}

  // ── translations ──────────────────────────────────────────────────────────
loadTranslations(): void {
  this.translate
    .get([
      'DATA_TABLE.NAME',
      'DATA_TABLE.START_DATE',
      'DATA_TABLE.END_DATE',
      'DATA_TABLE.STATUS',
      'COMMON.ACTIVE',
      'COMMON.INACTIVE',
    ])
    .pipe(takeUntil(this.destroy$))
    .subscribe((t) => {
      this.translatedHeaders = {
        name:      t['DATA_TABLE.NAME'],
        startDate: t['DATA_TABLE.START_DATE'],
        endDate:   t['DATA_TABLE.END_DATE'],
        status:    t['DATA_TABLE.STATUS'],
      };
      this.buildColumns();

      // ✅ Délai pour laisser le temps à buildColumns de s'appliquer
      setTimeout(() => {
        this.updatePeriodsDisplay();
      }, 50);
    });
}

  buildColumns(): void {
    this.periodColumns = [
      {
        field:      'name',
        headerText: this.translatedHeaders.name,
        width:      220,
        textAlign:  this.isRTL ? 'Right' : 'Left',
      },
      {
        field:      'startDate',
        headerText: this.translatedHeaders.startDate,
        width:      150,
        textAlign:  'Center',
      },
      {
        field:      'endDate',
        headerText: this.translatedHeaders.endDate,
        width:      150,
        textAlign:  'Center',
      },
      {
        field:      'isActiveLabel',  // ✅ champ transformé
        headerText: this.translatedHeaders.status,
        width:      120,
        textAlign:  'Center',
      },
      {
        field:      'id',
        headerText: '',
        width:      200,
        freeze:     'Right',
        textAlign:  'Center',
        template:   'actionTemplate',
      },
    ];
  }

  // ✅ Transformer isActive (boolean) en label traduit
updatePeriodsDisplay(): void {
  if (!this.periods.length) return;

  // ✅ Vider d'abord pour forcer app-custom-tab à re-render
  this.periodsForDisplay = [];
  this.cdr.detectChanges();

  // ✅ Reconstruire avec toutes les propriétés explicites
  setTimeout(() => {
    this.periodsForDisplay = this.periods.map(p => ({
      id:            p.id,
      name:          p.name,
      startDate:     p.startDate,
      endDate:       p.endDate,
      isActive:      Boolean(p.isActive),  // ✅ forcer boolean explicite
      createdAt:     p.createdAt,
      updatedAt:     p.updatedAt,
      isActiveLabel: p.isActive
        ? this.translate.instant('COMMON.ACTIVE')
        : this.translate.instant('COMMON.INACTIVE'),
    }));
    this.cdr.detectChanges();
  }, 0);
}

  // ── data ──────────────────────────────────────────────────────────────────
  loadPeriods(): void {
    this.isLoading = true;
    this.error     = null;

    this.periodService.getAcademicPeriods()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe({
        next: (response) => {
          this.periods = response?.data ?? [];
          this.updatePeriodsDisplay(); // ✅ transformer après chargement
        },
        error: () => {
          this.translate
            .get('ADMIN.ERROR_LOADING_PERIODS')
            .pipe(takeUntil(this.destroy$))
            .subscribe((msg) => {
              this.error = msg;
              this.swalService.warning(msg);
            });
        },
      });
  }

  // ── view modal ────────────────────────────────────────────────────────────
  view(period: AcademicPeriod): void {
    this.viewPeriod    = period;
    this.showViewModal = true;
  }

  closeViewModal(): void {
    this.showViewModal = false;
    this.viewPeriod    = null;
  }

  // ── add modal ─────────────────────────────────────────────────────────────
  openAddModal(): void {
    this.showAddModal = true;
  }

  closeAddModal(): void {
    this.showAddModal = false;
    this.newPeriod    = { name: '', startDate: '', endDate: '' };
  }

  createPeriod(): void {
    if (this.newPeriod.endDate < this.newPeriod.startDate) {
      this.translate
        .get('ADMIN.DATE_RANGE_ERROR')
        .pipe(takeUntil(this.destroy$))
        .subscribe((msg) => this.swalService.warning(msg));
      return;
    }

    this.isSaving = true;
    this.periodService.createPeriod(this.newPeriod)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isSaving = false)),
      )
      .subscribe({
        next: () => {
          this.translate
            .get('ADMIN.PERIOD_CREATED_SUCCESS')
            .pipe(takeUntil(this.destroy$))
            .subscribe((msg) => {
              this.swalService.success(msg);
              this.closeAddModal();
              this.loadPeriods();
            });
        },
        error: () => {
          this.translate
            .get('ADMIN.ERROR_CREATING_PERIOD')
            .pipe(takeUntil(this.destroy$))
            .subscribe((msg) => this.swalService.warning(msg));
        },
      });
  }

  // ── edit modal ────────────────────────────────────────────────────────────
  editPeriod(period: AcademicPeriod): void {
    this.selectedPeriod = period;
    this.editPeriodData = {
      name:      period.name,
      startDate: period.startDate,
      endDate:   period.endDate,
    };
    this.showEditModal = true;
  }

  closeEditModal(): void {
    this.showEditModal  = false;
    this.selectedPeriod = null;
    this.editPeriodData = { name: '', startDate: '', endDate: '' };
  }

  updatePeriod(): void {
    if (!this.selectedPeriod) return;

    if (this.editPeriodData.endDate < this.editPeriodData.startDate) {
      this.translate
        .get('ADMIN.DATE_RANGE_ERROR')
        .pipe(takeUntil(this.destroy$))
        .subscribe((msg) => this.swalService.warning(msg));
      return;
    }

    this.isSaving = true;
    this.periodService.updatePeriod(this.selectedPeriod.id, this.editPeriodData)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isSaving = false)),
      )
      .subscribe({
        next: () => {
          this.translate
            .get('ADMIN.PERIOD_UPDATED_SUCCESS')
            .pipe(takeUntil(this.destroy$))
            .subscribe((msg) => {
              this.swalService.success(msg);
              this.closeEditModal();
              this.loadPeriods();
            });
        },
        error: () => {
          this.translate
            .get('ADMIN.ERROR_UPDATING_PERIOD')
            .pipe(takeUntil(this.destroy$))
            .subscribe((msg) => this.swalService.warning(msg));
        },
      });
  }

  // ── actions ───────────────────────────────────────────────────────────────
  activate(period: AcademicPeriod): void {
    this.translate
      .get('ADMIN.CONFIRM_ACTIVATE_PERIOD')
      .pipe(takeUntil(this.destroy$))
      .subscribe((msg) => {
        const message = msg.replace('{name}', period.name);
        this.swalService.confirm(message, () => {
          this.periodService.activatePeriod(period.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.translate
                  .get('ADMIN.PERIOD_ACTIVATED_SUCCESS')
                  .pipe(takeUntil(this.destroy$))
                  .subscribe((m) => {
                    this.swalService.success(m);
                    this.loadPeriods();
                  });
              },
              error: () => {
                this.translate
                  .get('ADMIN.ERROR_ACTIVATING_PERIOD')
                  .pipe(takeUntil(this.destroy$))
                  .subscribe((m) => this.swalService.warning(m));
              },
            });
        });
      });
  }

  deactivate(period: AcademicPeriod): void {
    this.translate
      .get('ADMIN.CANNOT_DEACTIVATE_PERIOD')
      .pipe(takeUntil(this.destroy$))
      .subscribe((msg) => this.swalService.warning(msg));
  }

  deletePeriod(period: AcademicPeriod): void {
    if (period.isActive) {
      this.translate
        .get('ADMIN.CANNOT_DELETE_ACTIVE_PERIOD')
        .pipe(takeUntil(this.destroy$))
        .subscribe((msg) => this.swalService.warning(msg));
      return;
    }

    this.translate
      .get('ADMIN.CONFIRM_DELETE_PERIOD')
      .pipe(takeUntil(this.destroy$))
      .subscribe((msg) => {
        const message = msg.replace('{name}', period.name);
        this.swalService.confirm(message, () => {
          this.periodService.deletePeriod(period.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.translate
                  .get('ADMIN.PERIOD_DELETED_SUCCESS')
                  .pipe(takeUntil(this.destroy$))
                  .subscribe((m) => {
                    this.swalService.success(m);
                    this.loadPeriods();
                  });
              },
              error: () => {
                this.translate
                  .get('ADMIN.ERROR_DELETING_PERIOD')
                  .pipe(takeUntil(this.destroy$))
                  .subscribe((m) => this.swalService.warning(m));
              },
            });
        });
      });
  }
}